import { addDays, daysBetween } from '../../utility/date/relative'
import { logger } from '../../utility/logger'
import { MoneyType, priceToMoneyType } from '../../utility/money'
import { keysToCamel } from '../../utility/toCamel'

export interface CalendarDay {
  selected: boolean
  date: string
  fulldate: string
  arrive: string
  depart: string
  is_available: boolean
  price: MoneyType
}

interface FetchParams {
  pitchtypeId: string
  start: string
  end: string
  adults: string
  childAges: string
  lang: string
}

function calculateDateRange(arrive: string, depart: string) {
  const nights = Math.min(daysBetween(depart, arrive) * 2, 7)
  const start = addDays(-nights, arrive)
  const end = addDays(nights, depart)
  return { start, end, nights }
}

export async function fetchAlternativeStays({
  pitchtypeId,
  start,
  end,
  adults,
  childAges,
  lang,
}: FetchParams): Promise<CalendarDay[]> {
  const urlPrefix = lang === 'en-gb' ? '' : `/${lang}`
  const { start: rangeStart, end: rangeEnd } = calculateDateRange(start, end)
  const queryParams = new URLSearchParams({
    pitchtype_id: pitchtypeId,
    arrive: start,
    depart: end,
    adults,
    child_ages: childAges,
    no_html: 'true',
    start: rangeStart,
    end: rangeEnd,
  })

  try {
    const data = await $fetch(
      `/_/fallback${urlPrefix}/_/endpoints/chargetype_calendar_scroll/?${queryParams.toString()}`,
      {
        headers: { 'x-requested-with': 'XMLHttpRequest' },
        timeout: 30000,
      },
    )
    return processCalendarData(data.calendar_slice_data, start)
  } catch (error) {
    logger('Error fetching alternative stays:', error)
    return []
  }
}

function processCalendarData(
  calendarData: CalendarDay[][],
  targetStart: string,
): CalendarDay[] {
  const today = new Date()

  function isWithinThreeDays(date: string): boolean {
    return Math.abs(daysBetween(date, targetStart)) <= 3
  }

  return calendarData
    .flat()
    .filter(
      (day: CalendarDay) =>
        day.is_available &&
        new Date(day.arrive) >= today &&
        isWithinThreeDays(day.arrive),
    )
    .sort(
      (a: CalendarDay, b: CalendarDay) =>
        Math.abs(daysBetween(a.arrive, targetStart)) -
        Math.abs(daysBetween(b.arrive, targetStart)),
    )
    .slice(0, 3)
    .map((day: CalendarDay) => ({
      ...day,
      price: priceToMoneyType(keysToCamel(day.price)),
    }))
}
